/* ═══════════════════════════════════════════════════════════════════
   [D-426] Порівняння ДО / ПІСЛЯ для «Посилення» (дашборд).

   Запит OWNER 29.09: «як в Autopilot Studio — прев'ю порівняння з
   оригіналом і показ кожної функції». Окремий файл навмисно: сторінка
   HyperSettingsPage велика й жива, тут лише підключення в кількох
   місцях — щоб нове прев'ю не могло зламати налаштування.

   ЩО ПОКАЗУЄ
   • Два екрани на ТВОЄМУ відео: ліворуч оригінал, праворуч результат.
     Відео обирається з ПК і лишається в браузері (IndexedDB) — на сервер
     не йде, наступного разу вже на місці.
   • Рух камери — не вигаданий: точки кривої з /uniq/preview_curve, тобто
     той самий код, що робить відео (D-355). Віддалення нижче оригіналу
     (D-424) показується з розмитими краями, як у готовому ролику.
   • Кожна функція, яку вмикає сторінка: фон, кольоровий фон, врізка
     автора, субтитри, картки, стрілки, дзеркало (парні версії),
     переозвучка, мікро-ефекти (плями, вигин).
   • Змінив налаштування — прев'ю перескакує на момент, де функція
     починається, і підписує, що саме змінилось.

   🔴 Усе, крім кривої, — НАБЛИЖЕННЯ для очей (CSS поверх відео), а не
   рендер. Справжній вигляд дає лише готовий ролик.
   ═══════════════════════════════════════════════════════════════════ */
import React, { useEffect, useMemo, useRef, useState } from 'react';

const K = {
  card: '#16202c', line: 'rgba(0,229,160,.22)', txt: '#eaf6ff', dim: '#a8bece',
  neon: '#00e5a0', coral: '#ff7a59', blue: '#4da6ff', pink: '#ff5ca8', cyan: '#22d3ee',
};

// ── відео прев'ю живе в браузері (IndexedDB), не на сервері ──
const DBN = 'hyper_preview_v1', STN = 'clip';
function idbOpen() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DBN, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STN);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
async function idbGet() {
  try {
    const db = await idbOpen();
    return await new Promise(res => {
      const q = db.transaction(STN, 'readonly').objectStore(STN).get('clip');
      q.onsuccess = () => res(q.result || null); q.onerror = () => res(null);
    });
  } catch (e) { return null; }
}
async function idbPut(blob) {
  try {
    const db = await idbOpen();
    await new Promise(res => {
      const tx = db.transaction(STN, 'readwrite');
      tx.objectStore(STN).put(blob, 'clip'); tx.oncomplete = res; tx.onerror = res;
    });
  } catch (e) { /* немає місця — просто не запам'ятаємо */ }
}

const rnd = (seed) => { const x = Math.sin(seed * 9301 + 49297) * 233280; return x - Math.floor(x); };
// свої числа на версію — як рознесення по смугах у бота (для очей, не точні значення)
function verParams(v) {
  const r = i => rnd(v * 17 + i);
  return { zoom: 3 + 11 * r(1), offx: (r(2) - .5) * 2, offy: (r(3) - .5) * 2,
           hue: (r(4) < .5 ? -1 : 1) * (2 + 2 * r(5)), con: .92 + .23 * r(6),
           bri: -.035 + .055 * r(7), sat: .88 + .27 * r(8) };
}
const truthy = (v) => ['1', 'true', 'on', 't'].includes(String(v == null ? '' : v).toLowerCase());
function featOn(s, feats, k) {
  const v = s['uniq_hyper_' + k];
  if (v !== undefined && v !== null && String(v) !== '') return truthy(v);
  const f = (feats || []).find(x => x.key === k);
  return f ? truthy(f.value_default) : false;
}
// яку функцію показати, коли збережено ці ключі
function keyToEffect(keys) {
  for (const k of keys || []) {
    if (k === 'uniq_dir_profile' || k === 'uniq_dir_manual') return 'curve';
    if (k.startsWith('uniq_dir_intro')) return 'intro';
    if (k.startsWith('uniq_dir_scene')) return 'drift';
    if (k.startsWith('uniq_dir_')) return 'cuts';
    if (k.startsWith('uniq_fx_grid')) return 'grid';
    if (k.startsWith('uniq_fx_lens')) return 'lens';
    if (k === 'uniq_bg_gradient') return 'bg_grad';
    if (k === 'uniq_broll_cards') return 'cards';
    if (k === 'uniq_arrows') return 'arrows';
    if (k === 'uniq_mirror') return 'mirror';
    if (k.startsWith('uniq_dub')) return 'dub';
    const m = k.match(/^uniq_hyper_(bg|author|subs|director|mirror|cards|bg_grad|arrows|gentle)$/);
    if (m) return m[1] === 'director' ? 'cuts' : m[1] === 'gentle' ? 'author' : m[1];
  }
  return null;
}
const EFF_TITLE = { curve: 'Крива руху камери', intro: 'Вступний рух', drift: 'Зум у сцені', cuts: 'Зміна планів',
  zoomout: 'Віддалення', grid: 'Плями (мікро)', lens: 'Вигин (мікро)', bg: 'Фон: розмиття', bg_grad: 'Кольоровий фон',
  author: 'Автор у врізці', subs: 'Субтитри', cards: 'Картки-ілюстрації', arrows: 'Стрілки',
  mirror: 'Дзеркало (парні версії)', dub: 'Переозвучка', uniq: 'Унікалізація' };

let _styleDone = false;
function injectStyle() {
  if (_styleDone) return; _styleDone = true;
  const st = document.createElement('style');
  st.textContent = `@keyframes hcPop{0%{transform:translate(-50%,0) scale(.8);opacity:0}100%{transform:translate(-50%,0) scale(1);opacity:1}}
@keyframes hcBob{50%{margin-top:6px}}`;
  document.head.appendChild(st);
}

export default function HyperCompare({ s, feats, curve, seed = 0, onSeed, onDuration, focus, onOpenCurve, compact = false, srcUrl = '' }) {
  const [url, setUrl] = useState('');
  const [dur, setDur] = useState(12);
  const [playing, setPlaying] = useState(true);
  const [mag, setMag] = useState(false);
  const [tick, setTick] = useState(0);         // перемальовування підписів ~8 разів/с
  const [bubble, setBubble] = useState('');
  const T = useRef(0);
  const refs = { vB: useRef(null), vA: useRef(null), vBg: useRef(null), vIn: useRef(null), wrap: useRef(null),
                 bgv: useRef(null), grid: useRef(null), ruler: useRef(null) };
  const ver = (Number(seed) || 0) + 1;
  const fileRef = useRef(null);
  useEffect(injectStyle, []);

  // відео з браузера
  useEffect(() => {
    let alive = true, u = '';
    // srcUrl — готове посилання замість файлу з браузера (тести, майбутнє «відео з бота»)
    if (srcUrl) { setUrl(srcUrl); return () => { alive = false; }; }
    idbGet().then(b => { if (alive && b) { u = URL.createObjectURL(b); setUrl(u); } });
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [srcUrl]);
  const pick = (f) => {
    if (!f) return;
    idbPut(f);
    setUrl(old => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(f); });
  };

  const on = (k) => featOn(s || {}, feats, k);
  const flag = (k) => truthy((s || {})[k]);
  const pts = (curve && curve.points) || [];
  const cDur = pts.length ? (pts[pts.length - 1].t || 1) : dur;
  const zAt = (t) => {
    if (!pts.length) return { z: 1, x: .5 };
    const tt = t * cDur / Math.max(.1, dur);
    for (let i = 1; i < pts.length; i++) if (pts[i].t >= tt) {
      const a = pts[i - 1], b = pts[i], k = (tt - a.t) / Math.max(1e-6, b.t - a.t);
      return { z: a.z + (b.z - a.z) * k, x: a.x + (b.x - a.x) * k };
    }
    return pts[pts.length - 1];
  };

  // ── список функцій і коли вони на відео ──
  const effects = useMemo(() => {
    const D = dur, sc = cDur > 0 ? D / cDur : 1, m = (curve && curve.marks) || {}, E = [];
    E.push({ k: 'uniq', c: '#7c5cff', w: [[0, D]] });
    if (m.intro && m.intro.on) E.push({ k: 'intro', c: K.blue, w: [[0, Math.min(m.intro.dur * sc, D)]] });
    if ((m.drift || []).length) E.push({ k: 'drift', c: K.neon, w: m.drift.map(([a, b]) => [a * sc, b * sc]) });
    if ((m.cuts || []).length) E.push({ k: 'cuts', c: '#00b4d8', w: m.cuts.map(t => [t * sc, t * sc + .25]) });
    const low = []; let st = null;
    pts.forEach(p => { if (p.z < .995 && st === null) st = p.t; if (p.z >= .995 && st !== null) { low.push([st * sc, p.t * sc]); st = null; } });
    if (st !== null) low.push([st * sc, D]);
    if (low.length) E.push({ k: 'zoomout', c: K.pink, w: low });
    if (s && (s.uniq_fx_grid_on === undefined || flag('uniq_fx_grid_on'))) E.push({ k: 'grid', c: '#9d8df1', w: [[0, D]] });
    if (s && (s.uniq_fx_lens_on === undefined || flag('uniq_fx_lens_on'))) E.push({ k: 'lens', c: '#b39dff', w: [[0, D]] });
    const grad = on('bg_grad') || flag('uniq_bg_gradient');
    if (grad) E.push({ k: 'bg_grad', c: '#2a9d8f', w: [[0, D * .42], [D * .62, D]] });
    else if (on('bg')) E.push({ k: 'bg', c: '#52b69a', w: [[0, D * .42], [D * .62, D]] });
    if (on('author') && !on('gentle')) E.push({ k: 'author', c: '#b5e48c', w: [[D * .12, D]] });
    if (on('subs')) E.push({ k: 'subs', c: '#ffe600', w: [[.3, D]] });
    if (on('cards') || flag('uniq_broll_cards')) E.push({ k: 'cards', c: '#f7b267', w: [[D * .38, Math.min(D, D * .38 + 2.6)]] });
    if (on('arrows') || flag('uniq_arrows')) E.push({ k: 'arrows', c: '#6fd3ff', w: [[D * .2, D * .2 + 1.1], [D * .66, D * .66 + 1.1]] });
    if (on('mirror') || flag('uniq_mirror')) E.push({ k: 'mirror', c: '#90e0ef', w: [[0, D]] });
    if (flag('uniq_dub_enabled')) E.push({ k: 'dub', c: '#ffd166', w: [[.3, D]] });
    return E;
  }, [s, feats, curve, dur]);
  const has = (k) => effects.some(e => e.k === k);
  const within = (k, t) => { const e = effects.find(x => x.k === k); return !!e && e.w.some(([a, b]) => t >= a && t < b); };

  // ── «що змінилось» → перескочити на момент ──
  const lastFocus = useRef(0);
  useEffect(() => {
    if (!focus || !focus.n || focus.n === lastFocus.current) return;
    lastFocus.current = focus.n;
    const k = focus.t != null ? 'curve' : keyToEffect(focus.keys);
    if (!k) return;
    if (focus.t != null) { T.current = Math.max(0, Math.min(dur - .05, focus.t * dur / Math.max(.1, cDur))); setPlaying(false); }
    const e = effects.find(x => x.k === k) || (k === 'curve' ? effects.find(x => x.k === 'zoomout' || x.k === 'intro') : null);
    if (k === 'mirror' && ver % 2 === 1 && onSeed) onSeed(1);
    if (e && focus.t == null) T.current = e.w[0][0];
    setBubble(e || k === 'curve' ? `▶ ${EFF_TITLE[k] || k}${e && focus.t == null ? `: з ${e.w[0][0].toFixed(1)} с` : ''}`
                                 : `${EFF_TITLE[k] || k} вимкнено — порівняй з ДО`);
    const id = setTimeout(() => setBubble(''), 2800);
    return () => clearTimeout(id);
  }, [focus && focus.n]);

  // сітка плям — один раз на версію/силу
  useEffect(() => {
    const c = refs.grid.current; if (!c) return;
    const g = c.getContext('2d'), W = c.width, H = c.height, img = g.createImageData(W, H);
    const amp = Number((s || {}).uniq_fx_grid_amp || 7) * .6 * (mag ? 3 : 1);
    const pmin = Number((s || {}).uniq_fx_grid_period_min || 140), pmax = Number((s || {}).uniq_fx_grid_period_max || 210);
    const px = (pmin + (pmax - pmin) * rnd(ver * 5)) / 1080 * W, py = (pmin + (pmax - pmin) * rnd(ver * 7)) / 1920 * H;
    const fx = 6.28 * rnd(ver), fy = 6.28 * rnd(ver + 9);
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++)
      img.data[(y * W + x) * 4 + 3] = (Math.sin(2 * Math.PI * x / px + fx) * Math.sin(2 * Math.PI * y / py + fy) + 1) / 2 * amp;
    g.putImageData(img, 0, 0);
  }, [ver, mag, s && s.uniq_fx_grid_amp, url]);

  // ── годинник: ПІСЛЯ веде час, відео підлаштовуються ──
  useEffect(() => {
    let raf = 0, last = performance.now(), lastUi = 0;
    const sync = (v, t, run) => {
      if (!v) return;
      if (Math.abs(v.currentTime - t) > .25) { try { v.currentTime = t; } catch (e) { /* ще не готове */ } }
      if (run && v.paused) v.play().catch(() => {});
      if (!run && !v.paused) v.pause();
    };
    const loop = (now) => {
      const dt = Math.min(.1, (now - last) / 1000); last = now;
      if (playing && url) { T.current += dt; if (T.current >= dur) T.current = 0; }
      const t = T.current, vp = verParams(ver), m = mag ? 2 : 1, cz = zAt(t);
      [refs.vB, refs.vA, refs.vBg, refs.vIn].forEach(r => sync(r.current, t, playing && !!url));
      const w = refs.wrap.current;
      if (w) {
        const mir = has('mirror') && ver % 2 === 0 ? -1 : 1;
        const bgOn = (has('bg') && within('bg', t)) || (has('bg_grad') && within('bg_grad', t));
        const z = (1 + vp.zoom * m / 100) * cz.z;
        const shift = (cz.x - .5) * (1 - 1 / Math.max(1, cz.z)) * -100;
        w.style.transform = `translate(${vp.offx * 3 * m + shift}%, ${vp.offy * 3 * m}%) scale(${z * mir}, ${z})`;
        w.style.filter = `hue-rotate(${vp.hue * (mag ? 4 : 1)}deg) contrast(${1 + (vp.con - 1) * (mag ? 4 : 1)}) brightness(${1 + vp.bri * (mag ? 4 : 1)}) saturate(${1 + (vp.sat - 1) * (mag ? 4 : 1)})`;
        // автора бот вирізає моделлю; тут — м'яка маска довкола нього (наближення)
        const mask = bgOn ? 'radial-gradient(ellipse 58% 62% at 50% 58%, #000 55%, transparent 88%)' : 'none';
        w.style.webkitMaskImage = mask; w.style.maskImage = mask;
        const bg = refs.bgv.current;
        if (bg) {
          const showBg = bgOn || z < 1;
          bg.style.display = showBg ? 'block' : 'none';
          bg.style.background = has('bg_grad') && bgOn ? 'linear-gradient(160deg,#7c5cff,#22d3ee 60%,#ff5ca8)' : '#000';
          if (refs.vBg.current) refs.vBg.current.style.opacity = has('bg_grad') && bgOn ? 0 : 1;
        }
      }
      if (now - lastUi > 120) { lastUi = now; setTick(x => x + 1); }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playing, url, dur, ver, mag, effects, curve]);

  const seekTo = (t) => { T.current = Math.max(0, Math.min(dur - .03, t)); setTick(x => x + 1); };
  const scrub = (e) => {
    const el = refs.ruler.current; if (!el) return;
    setPlaying(false);
    const go = (ev) => { const r = el.getBoundingClientRect(); seekTo((ev.clientX - r.left) / r.width * dur); };
    go(e);
    const up = () => { window.removeEventListener('pointermove', go); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', go); window.addEventListener('pointerup', up);
  };

  const t = T.current, cz = zAt(t), zPct = (cz.z - 1) * 100;
  const phoneW = compact ? 132 : 172, phoneH = Math.round(phoneW * 16 / 9);
  const auShape = ['50%', '0', '12px'][(ver - 1) % 3];
  const words = ['Сьогодні', 'готуємо', 'швидку', 'піцу', 'вдома'];
  const wk = Math.floor(t * 2.2) % words.length;
  const btn = (active) => ({ border: `1px solid ${active ? K.neon : K.line}`, background: active ? 'rgba(0,229,160,.14)' : 'transparent',
    color: active ? K.neon : K.dim, borderRadius: 8, fontSize: 11.5, padding: '4px 8px', cursor: 'pointer', fontWeight: 700 });
  const phone = (after, children) => (
    <div style={{ width: phoneW, height: phoneH, borderRadius: 18, padding: 5, background: '#07060d', position: 'relative',
                  border: `1px solid ${after ? 'rgba(34,211,238,.5)' : 'rgba(168,190,206,.25)'}` }}>
      <div style={{ position: 'absolute', inset: 5, borderRadius: 14, overflow: 'hidden', background: '#000' }}>{children}</div>
    </div>);
  const vidStyle = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };
  const fx = (on2, style, content) => on2 ? <div style={{ position: 'absolute', pointerEvents: 'none', ...style }}>{content}</div> : null;
  const inSrc = true;

  return (
    <div style={{ color: K.txt }} data-tick={tick}>
      {/* ── верх: версії, лупа, відео ── */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        {[1, 2, 3, 4].map(v => (
          <button key={v} style={btn(v === ver)} onClick={() => onSeed && onSeed(v - 1)}
                  title={has('mirror') && v % 2 === 0 ? 'дзеркальна версія' : ''}>в{v}{has('mirror') && v % 2 === 0 ? ' ⇋' : ''}</button>
        ))}
        <button style={btn(mag)} onClick={() => setMag(x => !x)} title="Для очей: підсилює непомітні зміни. На відео вони слабкі.">🔍</button>
        <button style={{ ...btn(false), marginLeft: 'auto' }} onClick={() => fileRef.current && fileRef.current.click()}>
          📁 {url ? 'Інше відео' : 'Обрати відео'}</button>
        <input ref={fileRef} type="file" accept="video/*" style={{ display: 'none' }}
               onChange={e => pick(e.target.files && e.target.files[0])} />
      </div>

      {!url ? (
        <div onClick={() => fileRef.current && fileRef.current.click()}
             style={{ border: `2px dashed ${K.line}`, borderRadius: 12, padding: '26px 14px', textAlign: 'center', cursor: 'pointer',
                      color: K.dim, fontSize: 12.5, lineHeight: 1.5 }}>
          🎬 <b style={{ color: K.txt }}>Обери будь-яке відео з ПК</b><br />
          Воно лишається в цьому браузері й на сервер не відправляється.<br />Наступного разу буде вже тут.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: 1.5, color: K.dim, marginBottom: 4 }}>ДО</div>
              {phone(false, <video ref={refs.vB} src={url} muted playsInline preload="auto" style={vidStyle}
                                   onLoadedMetadata={e => { const d = e.target.duration || 12; setDur(d); if (onDuration) onDuration(Math.max(3, Math.min(300, Math.round(d)))); }} />)}
            </div>
            <div style={{ textAlign: 'center', position: 'relative' }}>
              <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: 1.5, color: K.cyan, marginBottom: 4 }}>ПІСЛЯ · в{ver}</div>
              {bubble && <div style={{ position: 'absolute', left: '50%', top: 26, zIndex: 5, whiteSpace: 'nowrap', background: K.cyan, color: '#04202a',
                                       fontSize: compact ? 10 : 11.5, fontWeight: 800, padding: '4px 9px', borderRadius: 9, transform: 'translate(-50%,0)', boxShadow: '0 6px 16px rgba(0,0,0,.45)',
                                       animation: 'hcPop .25s ease' }}>{bubble}</div>}
              {phone(true, <>
                <div ref={refs.bgv} style={{ position: 'absolute', inset: '-10%', display: 'none' }}>
                  <video ref={refs.vBg} src={url} muted playsInline preload="auto" style={{ ...vidStyle, filter: 'blur(9px) brightness(.8)' }} />
                </div>
                <div ref={refs.wrap} style={{ position: 'absolute', inset: 0, transformOrigin: '50% 45%' }}>
                  <video ref={refs.vA} src={url} muted playsInline preload="auto" style={vidStyle} />
                </div>
                <canvas ref={refs.grid} width={phoneW - 10} height={phoneH - 10}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', display: has('grid') ? 'block' : 'none' }} />
                {has('lens') && mag && <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', boxShadow: 'inset 0 0 18px 6px rgba(0,0,0,.35)', borderRadius: 14 }} />}
                {/* врізка змонтована завжди (лише ховається) — інакше її відео вантажилось би в мить показу й було порожнім */}
                <div style={{ position: 'absolute', pointerEvents: 'none', right: 8, top: 34, width: 58, height: auShape === '12px' ? 74 : 58,
                     borderRadius: auShape === '0' ? 0 : auShape, overflow: 'hidden', border: auShape === '0' ? 'none' : '3px solid #fff',
                     clipPath: auShape === '0' ? 'polygon(25% 3%,75% 3%,100% 50%,75% 97%,25% 97%,0 50%)' : 'none',
                     display: has('author') && within('author', t) ? 'block' : 'none' }}>
                  <video ref={refs.vIn} src={url} muted playsInline preload="auto" style={{ ...vidStyle, objectPosition: '50% 28%', transform: 'scale(1.6)' }} />
                </div>
                {fx(has('cards') && within('cards', t), { left: 10, top: '38%', width: 58, height: 44 },
                    <>{['#219ebc', '#52b69a', '#f25c54'].map((c, i) => <i key={i} style={{ position: 'absolute', inset: 0, borderRadius: 6, border: '2px solid #fff',
                      background: c, transform: `rotate(${(i - 1) * 8}deg) translate(${(i - 1) * 4}px,0)` }} />)}</>)}
                {fx(has('arrows') && within('arrows', t), { left: '22%', top: '44%', textAlign: 'center' },
                    <><div style={{ background: '#fff', color: '#111', fontWeight: 900, fontSize: 11, padding: '2px 6px', borderRadius: 5 }}>15 ХВИЛИН</div>
                      <div style={{ color: '#6fd3ff', fontSize: 20, lineHeight: 1, animation: 'hcBob 1s ease-in-out infinite' }}>⬆</div></>)}
                {fx(has('subs') && within('subs', t), { left: 6, right: 6, bottom: 34, textAlign: 'center', fontWeight: 800, fontSize: compact ? 10.5 : 12,
                     color: '#fff', textShadow: '0 2px 0 #000,0 0 6px #000' },
                    <>{words.slice(Math.max(0, wk - 2), wk).join(' ')} <span style={{ color: '#ffe600' }}>{words[wk]}</span></>)}
                {fx(has('dub') && inSrc, { left: 6, top: 6, fontSize: 9.5, fontWeight: 800, padding: '3px 6px', borderRadius: 6, background: 'rgba(0,0,0,.6)', color: '#ffd166' },
                    '🎙 переозвучка')}
              </>)}
            </div>
          </div>

          {/* ── керування часом ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
            <button style={btn(false)} onClick={() => seekTo(0)}>⏮</button>
            <button style={btn(false)} onClick={() => { setPlaying(false); seekTo(t - 1 / 30); }}>‹</button>
            <button style={{ ...btn(true), minWidth: 34 }} onClick={() => setPlaying(p => !p)}>{playing ? '⏸' : '▶'}</button>
            <button style={btn(false)} onClick={() => { setPlaying(false); seekTo(t + 1 / 30); }}>›</button>
            <span style={{ fontSize: 11.5, fontFamily: 'monospace', color: K.dim }}>{t.toFixed(2)} / {dur.toFixed(1)} с</span>
            <span style={{ marginLeft: 'auto', fontSize: 11.5, fontFamily: 'monospace', fontWeight: 700,
                           color: zPct < -0.3 ? K.pink : zPct > 0.3 ? K.neon : K.dim }}>
              {zPct >= 0 ? '+' : ''}{zPct.toFixed(1)}%</span>
          </div>

          {/* ── стрічка: шкала + рядок на кожну функцію ── */}
          <div style={{ marginTop: 6, userSelect: 'none' }}>
            <div ref={refs.ruler} onPointerDown={scrub}
                 style={{ position: 'relative', cursor: 'pointer', paddingBottom: 2 }}>
              {effects.map(e => (
                <div key={e.k} style={{ display: 'flex', alignItems: 'center', gap: 6, height: 15 }}
                     onDoubleClick={() => seekTo(e.w[0][0])}>
                  <span style={{ width: compact ? 88 : 104, fontSize: 10, color: K.dim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {EFF_TITLE[e.k]}</span>
                  <div style={{ flex: 1, height: 7, borderRadius: 4, background: 'rgba(255,255,255,.05)', position: 'relative' }}>
                    {e.w.map(([a, b], i) => <i key={i} style={{ position: 'absolute', top: 0, bottom: 0, borderRadius: 4, background: e.c,
                      left: `${a / dur * 100}%`, width: `${Math.max(1.2, (b - a) / dur * 100)}%` }} />)}
                  </div>
                </div>
              ))}
              <div style={{ position: 'absolute', top: 0, bottom: 0, width: 2, background: '#fff', boxShadow: '0 0 6px #fff', pointerEvents: 'none',
                            left: `calc(${compact ? 94 : 110}px + (100% - ${compact ? 94 : 110}px) * ${t / dur})` }} />
            </div>
            <div style={{ fontSize: 10, color: K.dim, marginTop: 3 }}>
              Тягни по рядках — обрати момент · подвійний клік — початок функції</div>
          </div>

          {/* ── крива зуму (клік — велика діаграма) ── */}
          {pts.length > 0 && (() => {
            const W = 300, H = 56, lo = Math.min(.8, ...pts.map(p => p.z)), hi = Math.max(1.3, ...pts.map(p => p.z));
            const y = z => (H - 4 - (z - lo) / (hi - lo) * (H - 8)).toFixed(1);
            const d = pts.map((p, i) => `${i ? 'L' : 'M'}${(p.t / cDur * W).toFixed(1)},${y(p.z)}`).join(' ');
            return (
              <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" onClick={() => onOpenCurve && onOpenCurve()}
                   style={{ width: '100%', height: H, marginTop: 6, borderRadius: 8, background: '#0b0f14', border: `1px solid ${K.line}`, cursor: 'zoom-in' }}>
                <rect x="0" y={y(1)} width={W} height={Math.max(0, H - 4 - y(1))} fill="rgba(255,92,168,.07)" />
                <line x1="0" x2={W} y1={y(1)} y2={y(1)} stroke="#ffd166" strokeDasharray="5 4" />
                <path d={d} fill="none" stroke={K.neon} strokeWidth="2" />
                <line x1={t / dur * W} x2={t / dur * W} y1="0" y2={H} stroke="#fff" strokeWidth="1.2" />
                <title>Клік — велика діаграма: тягни точки вгору (наближення) чи вниз (віддалення)</title>
              </svg>);
          })()}
          <div style={{ fontSize: 10, color: K.dim, marginTop: 3 }}>
            Жовта лінія — оригінал; нижче неї — віддалення (краї розмиті). Клік по кривій — редагувати.
            Усе, крім кривої, — наближений показ; точний вигляд — у готовому ролику.</div>
        </>
      )}
    </div>
  );
}
